import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  Typography,
  Paper,
  TextField,
  Select,
  MenuItem,
  FormControl,
  Modal, Box,
  InputLabel,
  Checkbox,
  FormControlLabel,
  Button,
  Grid,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import LoadingBar from "react-top-loading-bar";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const EditInventoryItem = () => {
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const navigate = useNavigate();
  const { itemId, branchId } = useParams();
  const [formData, setFormData] = useState({
    itemID: itemId || "",
    branchID: "",
    categoryID: "",
    itemName: "",
    itemCode: "",
    purchasedPrice: "",
    currentBalance: "",
    purchaseDate: null,
    sellingPrice: "", 
    vendorID: "",
    taxId: null,
    locationID: "",
    isDepreciable: false,
    usefulLifeYears: "",
    currentValue: "",
    isAutoGenerateItemCode: false,
  });
  const [branches, setBranches] = useState([]);
  const [categories, setCategories] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [taxRates, setTaxRates] = useState([]);
  const [locations, setLocations] = useState([]);
  const [errors, setErrors] = useState({});
  const [apiErrors, setApiErrors] = useState([]);
  const [loadingBarProgress, setLoadingBarProgress] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);
  const [responseData, setResponseData] = useState({
    itemId: null,
    itemCode: "",
  });

  // Helper function to add API errors
  const addApiError = (errorMessage) => {
    setApiErrors((prev) => [...prev, errorMessage]);
  };

  // Fetch dropdown data on component mount
  useEffect(() => {
    const fetchBranches = async () => {
      try {
        setLoadingBarProgress(20);
        const apiUrl = process.env.REACT_APP_BASE_URL;
        const response = await fetch(`${apiUrl}/Branch/GetBranch`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: sessionStorage.getItem("token"),
          },
        });

        if (response.ok) {
          const data = await response.json();
          console.log("Branches fetched:", data);
          if (Array.isArray(data)) {
            setBranches(data);
            if (!itemId && data.length === 1 && data[0]?.branchId) {
              setFormData((prev) => ({
                ...prev,
                branchID: String(data[0].branchId),
              }));
              console.log("Auto-filled branchID:", data[0].branchId);
            }
            if (data.length === 0) {
              addApiError("No branches available.");
            }
          } else {
            addApiError("Invalid branch data received.");
          }
        } else {
          let errorMessage = `Failed to fetch branches (Status: ${response.status})`;
          try {
            const errorData = await response.json();
            errorMessage = errorData.message || errorMessage;
          } catch {
            // Non-JSON response
          }
          addApiError(errorMessage);
          console.log("Failed to fetch branches, status:", response.status);
        }
      } catch (error) {
        console.error("Error fetching branches:", error);
        addApiError(`Error fetching branches: ${error.message}`);
      }
    };

    const fetchCategories = async () => {
      try {
        setLoadingBarProgress(20);
        const apiUrl = process.env.REACT_APP_BASE_URL;
        const response = await fetch(
          `${apiUrl}/inventory/Categories/GetMainCategory`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: sessionStorage.getItem("token"),
            },
            body: JSON.stringify({}),
          }
        );

        if (response.ok) {
          const data = await response.json();
          console.log("Categories fetched:", data);
          if (Array.isArray(data)) {
            setCategories(data);
            if (data.length === 0) {
              addApiError("No categories available.");
            }
          } else {
            addApiError("Invalid category data received.");
          }
        } else {
          let errorMessage = `Failed to fetch categories (Status: ${response.status})`;
          try {
            const errorData = await response.json();
            errorMessage = errorData.message || errorMessage;
          } catch {
            // Non-JSON response
          }
          addApiError(errorMessage);
          console.log("Failed to fetch categories, status:", response.status);
        }
      } catch (error) {
        console.error("Error fetching categories:", error);
        addApiError(`Error fetching categories: ${error.message}`);
      }
    };

    const fetchVendors = async () => {
      try {
        setLoadingBarProgress(20);
        const apiUrl = process.env.REACT_APP_BASE_URL;
        const response = await fetch(`${apiUrl}/Inventory/Vendor/GetVendors`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: sessionStorage.getItem("token"),
          },
          body: JSON.stringify({ vendorId: 0 }),
        });

        if (response.ok) {
          const data = await response.json();
          console.log("Vendors fetched:", data.vendors);
          if (Array.isArray(data.vendors)) {
            setVendors(data.vendors);
            if (data.vendors.length === 0) {
              addApiError("No vendors available.");
            }
          } else {
            addApiError("Invalid vendor data received.");
          }
        } else {
          let errorMessage = `Failed to fetch vendors (Status: ${response.status})`;
          try {
            const errorData = await response.json();
            errorMessage = errorData.message || errorMessage;
          } catch {
            // Non-JSON response
          }
          addApiError(errorMessage);
          console.log("Failed to fetch vendors, status:", response.status);
        }
      } catch (error) {
        console.error("Error fetching vendors:", error);
        addApiError(`Error fetching vendors: ${error.message}`);
      }
    };

    const fetchTaxRates = async () => {
      try {
        setLoadingBarProgress(20);
        const apiUrl = process.env.REACT_APP_BASE_URL;
        const response = await fetch(`${apiUrl}/Inventory/Item/GetTaxRates`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: sessionStorage.getItem("token"),
          },
        });

        if (response.ok) {
          const data = await response.json();
          console.log("Tax rates fetched:", data.taxRates);
          if (Array.isArray(data.taxRates)) {
            setTaxRates(data.taxRates);
            if (data.taxRates.length === 0) {
              addApiError("No tax rates available.");
            }
          } else {
            addApiError("Invalid tax rate data received.");
          }
        } else {
          let errorMessage = `Failed to fetch tax rates (Status: ${response.status})`;
          try {
            const errorData = await response.json();
            errorMessage = errorData.message || errorMessage;
          } catch {
            // Non-JSON response
          }
          addApiError(errorMessage);
          console.log("Failed to fetch tax rates, status:", response.status);
        }
      } catch (error) {
        console.error("Error fetching tax rates:", error);
        addApiError(`Error fetching tax rates: ${error.message}`);
      }
    };

    const fetchLocations = async () => {
      try {
        setLoadingBarProgress(20);
        const apiUrl = process.env.REACT_APP_BASE_URL;
        const response = await fetch(
          `${apiUrl}/Inventory/locations/GetLocations`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: sessionStorage.getItem("token"),
            },
            body: JSON.stringify({}),
          }
        );

        if (response.ok) {
          const data = await response.json();
          console.log("Locations fetched:", data.locations);
          if (Array.isArray(data.locations)) {
            setLocations(data.locations);
            if (data.locations.length === 0) {
              addApiError("No locations available.");
            }
          } else {
            addApiError("Invalid location data received.");
          }
        } else {
          let errorMessage = `Failed to fetch locations (Status: ${response.status})`;
          try {
            const errorData = await response.json();
            errorMessage = errorData.message || errorMessage;
          } catch {
            // Non-JSON response
          }
          addApiError(errorMessage);
          console.log("Failed to fetch locations, status:", response.status);
        }
      } catch (error) {
        console.error("Error fetching locations:", error);
        addApiError(`Error fetching locations: ${error.message}`);
      }
    };

    console.log("Starting dropdown data fetch...");
    Promise.all([
      fetchBranches(),
      fetchCategories(),
      fetchVendors(),
      fetchTaxRates(),
      fetchLocations(),
    ]).finally(() => {
      setLoadingBarProgress(50);
      console.log("Dropdown data fetch completed.");
    });
  }, []);

  // Fetch item data after dropdown data is loaded
  useEffect(() => {
    const fetchItemData = async () => {
      if (!itemId) return;

      try {
        setLoadingBarProgress(60);
        const apiUrl = process.env.REACT_APP_BASE_URL;
        const token = sessionStorage.getItem("token");
        const response = await fetch(
          `${apiUrl}/Inventory/Item/GetInventoryItems?itemId=${itemId}&Branch_Id=${branchId}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: token,
            },
          }
        );

        if (response.ok) {
          const data = await response.json();
          console.log("Item data fetched:", data);
          if (data && data.length > 0) {
            const item = data[0];
            setFormData({
              itemID: item.itemId?.toString() || itemId,
              branchID: item.branchId
                ? String(item.branchId)
                : branches.length === 1
                ? String(branches[0].branchId)
                : "",
              categoryID: item.categoryID ? String(item.categoryID) : "",
              itemName: item.itemName || "",
              itemCode: item.itemCode || "",
              purchasedPrice:
                item.purchasedPrice !== null ? String(item.purchasedPrice) : "",
                sellingPrice: item.sellingPrice !== null ? String(item.sellingPrice) : "",
              currentBalance:
                item.currentBalance !== null ? String(item.currentBalance) : "",
              purchaseDate: item.purchaseDate
                ? new Date(item.purchaseDate)
                : null,
              vendorID: item.vendorID ? String(item.vendorID) : "",
              taxId: item.taxId !== null ? item.taxId : null,
              locationID: item.locationID ? String(item.locationID) : "",
              isDepreciable: item.isDepreciable || false,
              usefulLifeYears:
                item.usefulLifeYears !== null
                  ? String(item.usefulLifeYears)
                  : "",
              currentValue:
                item.currentValue !== null ? String(item.currentValue) : "",
              isAutoGenerateItemCode: item.isAutoGenerateItemCode || false,
            });
            console.log("Form data set:", {
              itemID: item.itemId?.toString() || itemId,
              branchID: item.branchId
                ? String(item.branchId)
                : branches.length === 1
                ? String(branches[0].branchId)
                : "",
              categoryID: item.categoryID ? String(item.categoryID) : "",
              itemName: item.itemName || "",
              itemCode: item.itemCode || "",
              purchasedPrice:
                item.purchasedPrice !== null ? String(item.purchasedPrice) : "",
                sellingPrice: item.sellingPrice !== null ? String(item.sellingPrice) : "",
              currentBalance:
                item.currentBalance !== null ? String(item.currentBalance) : "",
              purchaseDate: item.purchaseDate
                ? new Date(item.purchaseDate)
                : null,
              vendorID: item.vendorID ? String(item.vendorID) : "",
              taxId: item.taxId !== null ? item.taxId : null,
              locationID: item.locationID ? String(item.locationID) : "",
              isDepreciable: item.isDepreciable || false,
              usefulLifeYears:
                item.usefulLifeYears !== null
                  ? String(item.usefulLifeYears)
                  : "",
              currentValue:
                item.currentValue !== null ? String(item.currentValue) : "",
              isAutoGenerateItemCode: item.isAutoGenerateItemCode || false,
            });
          } else {
            addApiError("No item data found.");
          }
        } else {
          let errorMessage = `Failed to fetch item data (Status: ${response.status})`;
          try {
            const errorData = await response.json();
            errorMessage = errorData.message || errorMessage;
          } catch {
            // Non-JSON response
          }
          addApiError(errorMessage);
          console.log("Failed to fetch item data, status:", response.status);
        }
      } catch (error) {
        console.error("Error fetching item:", error);
        addApiError(`Error fetching item: ${error.message}`);
      } finally {
        setLoadingBarProgress(100);
      }
    };

    if (
      branches.length > 0 &&
      categories.length > 0 &&
      vendors.length > 0 &&
      taxRates.length > 0 &&
      locations.length > 0
    ) {
      fetchItemData();
    }
  }, [itemId, branches, categories, vendors, taxRates, locations]);

  // Validation function
  const validateForm = () => {
    const newErrors = {};
    if (!formData.branchID) newErrors.branchID = "Branch is required";
    if (!formData.categoryID) newErrors.categoryID = "Category is required";
    if (!formData.itemName.trim()) newErrors.itemName = "Item Name is required";
    if (!formData.isAutoGenerateItemCode && !formData.itemCode.trim())
      newErrors.itemCode = "Item Code is required if not auto-generated";
    if (!formData.purchasedPrice)
      newErrors.purchasedPrice = "Purchased Price is required";
    else if (
      isNaN(formData.purchasedPrice) ||
      parseFloat(formData.purchasedPrice) < 0
    )
      newErrors.purchasedPrice =
        "Purchased Price must be a non-negative number";
    if (!formData.currentBalance)
      newErrors.currentBalance = "Current Balance is required";
    else if (
      isNaN(formData.currentBalance) ||
      parseFloat(formData.currentBalance) < 0
    )
      newErrors.currentBalance = "Current Balance cannot be negative";
    if (!formData.purchaseDate)
      newErrors.purchaseDate = "Purchase Date is required";
    if (!formData.vendorID) newErrors.vendorID = "Vendor is required";
    if (taxRates.length > 0 && formData.taxId === null)
      newErrors.taxId = "Tax is required";
    if (locations.length > 0 && !formData.locationID)
      newErrors.locationID = "Location is required";
    if (formData.isDepreciable && formData.usefulLifeYears === "")
      newErrors.usefulLifeYears =
        "Useful Life Years is required if depreciable";
    else if (
      formData.isDepreciable &&
      formData.usefulLifeYears !== "" &&
      (isNaN(formData.usefulLifeYears) ||
        parseInt(formData.usefulLifeYears) <= 0)
    )
      newErrors.usefulLifeYears = "Useful Life Years must be a positive number";
    if (
      formData.currentValue !== "" &&
      (isNaN(formData.currentValue) || parseFloat(formData.currentValue) < 0)
    )
      newErrors.currentValue = "Current Value cannot be negative";

    console.log("Validation errors:", newErrors);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form field changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    console.log(`Field changed: ${name}, Value: ${value}, Type: ${type}`);

    // Prevent negative values for purchasedPrice, currentBalance, and currentValue
    if (
      name === "purchasedPrice" ||
      name === "currentBalance" ||
      name === "currentValue" ||
      name === "sellingPrice"
    ) {
      // Allow empty input or valid non-negative numbers (including decimals)
      if (value === "" || (!isNaN(value) && parseFloat(value) >= 0)) {
        setFormData((prev) => ({
          ...prev,
          [name]: value,
        }));
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]:
          type === "checkbox"
            ? checked
            : name === "taxId"
            ? value === ""
              ? null
              : parseInt(value)
            : value,
        ...(name === "isDepreciable" && !checked
          ? { usefulLifeYears: "" }
          : {}),
      }));
    }

    setErrors((prev) => ({ ...prev, [name]: "" }));
    console.log("Updated formData:", formData);
  };
  
  const preventMinus = (e) => {
    if (e.key === "-" || e.key === "e") {
      e.preventDefault();
    }
  };

  // Handle date change
  const handleDateChange = (date) => {
    console.log("Date changed:", date);
    setFormData((prev) => ({ ...prev, purchaseDate: date }));
    setErrors((prev) => ({ ...prev, purchaseDate: "" }));
    console.log("Updated formData after date change:", formData);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    const newErrors = {};
    e.preventDefault();
    setApiErrors([]);
    console.log("Form submitted, current formData:", formData);

    const isValid = validateForm();
    console.log("Form validation result:", isValid);
    if (!isValid) return;

    if (!formData.sellingPrice)
  newErrors.sellingPrice = "Selling Price is required";
else if (isNaN(formData.sellingPrice) || parseFloat(formData.sellingPrice) < 0)
  newErrors.sellingPrice = "Selling Price cannot be negative";

    try {
      setLoadingBarProgress(30);
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const payload = {
        itemID: parseInt(formData.itemID) || 0,
        branchID: parseInt(formData.branchID),
        categoryID: parseInt(formData.categoryID) || 0,
        itemName: formData.itemName,
        itemCode: formData.itemCode,
        purchasedPrice: parseFloat(formData.purchasedPrice) || 0,
          sellingPrice: parseFloat(formData.sellingPrice) || 0,   // 👈 add this

        currentBalance: parseFloat(formData.currentBalance) || 0,
        purchaseDate: formData.purchaseDate
          ? formData.purchaseDate.toISOString()
          : null,
        vendorID: parseInt(formData.vendorID) || 0,
        taxId: formData.taxId !== null ? parseInt(formData.taxId) : 0,
        locationID: formData.locationID ? parseInt(formData.locationID) : 0,
        isAutoGenerateItemCode: formData.isAutoGenerateItemCode,
        isDepreciable: formData.isDepreciable,
        usefulLifeYears:
          formData.isDepreciable && formData.usefulLifeYears !== ""
            ? parseInt(formData.usefulLifeYears)
            : formData.isDepreciable
            ? 1
            : null,
        currentValue:
          formData.currentValue !== "" ? parseFloat(formData.currentValue) : 0,
      };

      console.log("Submitting payload:", payload);

      const token = sessionStorage.getItem("token");
      if (!token) {
        addApiError("Authentication token is missing. Please log in again.");
        console.log("No token found in sessionStorage.");
        return;
      }

const requestUrl = `${apiUrl}/Inventory/Item/InsertUpdateInventoryItem?Branch_id=${formData.branchID}`;
      console.log("Sending request to:", requestUrl);
      const response = await fetch(requestUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify(payload),
      });

      console.log("Response status:", response.status);
      const responseBody = await response.text();
      console.log("Server response body:", responseBody);

      if (response.ok) {
        let data;
        try {
          data = JSON.parse(responseBody);
          console.log("Parsed response data:", data);
          if (!data.itemId || !data.itemCode) {
            throw new Error(
              "Invalid response data: Missing itemId or itemCode."
            );
          }
        } catch {
          throw new Error("Failed to parse server response as JSON.");
        }

        setResponseData({
          itemId: data.itemId,
          itemCode: data.itemCode,
        });
       setShowSuccessModal(true);

      } else {
        let errorMessage = `Failed to update item (Status: ${response.status})`;
        try {
          const errorData = JSON.parse(responseBody);
          errorMessage = errorData.message || errorMessage;
        } catch {
          // Non-JSON response
        }
        addApiError(errorMessage);
        console.log("Submission error:", errorMessage);
      }
    } catch (error) {
      console.error("Error updating item:", error);
      addApiError(`Error updating item: ${error.message}`);
    } finally {
      setLoadingBarProgress(100);
      console.log("Submission process completed.");
    }
  };

  // Handle reset
  

  const handleDialogClose = () => {
    setOpenDialog(false);
    console.log("Dialog closed, navigating to /itemmaster");
    navigate(-1);
  };

  return (
    <div className="container">
      <LoadingBar
        progress={loadingBarProgress}
        color="rgb(152, 106, 182)"
        height={5}
      />
      <AppBar position="static" style={{ backgroundColor: "#0B1F3D" }}>
        <Toolbar>
          <Typography variant="h4" component="div" style={{fontWeight:'bold'}}>
            Edit Inventory Item
          </Typography>
        </Toolbar>
      </AppBar>

      <Paper elevation={3} style={{ padding: 16, marginTop: 6 }}>
        {apiErrors.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            {apiErrors.map((error, index) => (
              <Alert key={index} severity="error" style={{ marginBottom: 8 }}>
                {error}
              </Alert>
            ))}
          </div>
        )}
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={!!errors.branchID}>
                <InputLabel>Branch</InputLabel>
                <Select
                  name="branchID"
                  value={formData.branchID}
                  onChange={handleChange}
                  label="Branch"
                  disabled={branches.length <= 1}
                >
                  {branches.length > 0 ? (
                    branches.length > 1 && (
                      <MenuItem value="">Select Branch</MenuItem>
                    )
                  ) : (
                    <MenuItem value="" disabled>
                      No branches available
                    </MenuItem>
                  )}
                  {branches.map((branch) => (
                    <MenuItem
                      key={branch.branchId}
                      value={branch.branchId.toString()}
                    >
                      {branch.branchName}
                    </MenuItem>
                  ))}
                </Select>
                {errors.branchID && (
                  <Typography color="error" variant="caption">
                    {errors.branchID}
                  </Typography>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={!!errors.categoryID}>
                <InputLabel>Category</InputLabel>
                <Select
                  name="categoryID"
                  value={formData.categoryID}
                  onChange={handleChange}
                  label="Category"
                >
                  {categories.length > 0 ? (
                    <MenuItem value="">Select Category</MenuItem>
                  ) : (
                    <MenuItem value="" disabled>
                      No categories available
                    </MenuItem>
                  )}
                  {categories.map((category) => (
                    <MenuItem
                      key={category.mainCategoryId}
                      value={category.mainCategoryId.toString()}
                    >
                      {category.mainCategoryName}
                    </MenuItem>
                  ))}
                </Select>
                {errors.categoryID && (
                  <Typography color="error" variant="caption">
                    {errors.categoryID}
                  </Typography>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Item Name"
                name="itemName"
                value={formData.itemName}
                onChange={handleChange}
                error={!!errors.itemName}
                helperText={errors.itemName}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Item Code"
                name="itemCode"
                value={formData.itemCode}
                onChange={handleChange}
                error={!!errors.itemCode}
                helperText={errors.itemCode}
                disabled={formData.isAutoGenerateItemCode}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    name="isAutoGenerateItemCode"
                    checked={formData.isAutoGenerateItemCode}
                    onChange={handleChange}
                  />
                }
                label="Auto-generate Item Code"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Purchased Price"
                name="purchasedPrice"
                type="number"
                value={formData.purchasedPrice}
                onChange={handleChange}
                onKeyDown={preventMinus}
                error={!!errors.purchasedPrice}
                helperText={errors.purchasedPrice}
                inputProps={{ min: 0, step: "0.01" }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
  <TextField
    fullWidth
    label="Selling Price"
    name="sellingPrice"
    type="number"
    value={formData.sellingPrice}
    onChange={handleChange}
    error={!!errors.sellingPrice}
    helperText={errors.sellingPrice}
    inputProps={{ min: 0, step: "0.01" }}
  />
</Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Current Quantity"
                name="currentBalance"
                type="number"
                value={formData.currentBalance}
                onChange={handleChange}
                onKeyDown={preventMinus}
                error={!!errors.currentBalance}
                helperText={errors.currentBalance}
                inputProps={{ min: 0, step: "1" }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DatePicker
                selected={formData.purchaseDate}
                onChange={handleDateChange}
                dateFormat="MM/dd/yyyy"
                customInput={
                  <TextField
                    fullWidth
                    label="Purchase Date"
                    error={!!errors.purchaseDate}
                    helperText={errors.purchaseDate}
                  />
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={!!errors.vendorID}>
                <InputLabel>Vendor</InputLabel>
                <Select
                  name="vendorID"
                  value={formData.vendorID}
                  onChange={handleChange}
                  label="Vendor"
                >
                  {vendors.length > 0 ? (
                    <MenuItem value="">Select Vendor</MenuItem>
                  ) : (
                    <MenuItem value="" disabled>
                      No vendors available
                    </MenuItem>
                  )}
                  {vendors.map((vendor) => (
                    <MenuItem
                      key={vendor.vendorID}
                      value={vendor.vendorID.toString()}
                    >
                      {vendor.vendorName}
                    </MenuItem>
                  ))}
                </Select>
                {errors.vendorID && (
                  <Typography color="error" variant="caption">
                    {errors.vendorID}
                  </Typography>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl
                fullWidth
                error={!!errors.taxId}
                disabled={taxRates.length === 0}
              >
                <InputLabel>Tax</InputLabel>
                <Select
                  name="taxId"
                  value={formData.taxId !== null ? formData.taxId : ""}
                  onChange={handleChange}
                  label="Tax"
                >
                  {taxRates.length > 0 ? (
                    <MenuItem value="">Select Tax</MenuItem>
                  ) : (
                    <MenuItem value="" disabled>
                      No tax rates available
                    </MenuItem>
                  )}
                  {taxRates.map((tax) => (
                    <MenuItem key={tax.taxId} value={tax.taxId}>
                      {parseFloat(tax.tax).toFixed(2)}%
                    </MenuItem>
                  ))}
                </Select>
                {errors.taxId && (
                  <Typography color="error" variant="caption">
                    {errors.taxId}
                  </Typography>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={!!errors.locationID}>
                <InputLabel>Location</InputLabel>
                <Select
                  name="locationID"
                  value={formData.locationID}
                  onChange={handleChange}
                  label="Location"
                >
                  {locations.length > 0 ? (
                    <MenuItem value="">Select Location</MenuItem>
                  ) : (
                    <MenuItem value="" disabled>
                      No locations available
                    </MenuItem>
                  )}
                  {locations.map((location) => (
                    <MenuItem
                      key={location.locationID}
                      value={location.locationID.toString()}
                    >
                      {location.locationName}
                    </MenuItem>
                  ))}
                </Select>
                {errors.locationID && (
                  <Typography color="error" variant="caption">
                    {errors.locationID}
                  </Typography>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="isDepreciable"
                    checked={formData.isDepreciable}
                    onChange={handleChange}
                  />
                }
                label="Is Depreciable"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Useful Life Years"
                name="usefulLifeYears"
                type="number"
                value={formData.usefulLifeYears}
                onChange={handleChange}
                error={!!errors.usefulLifeYears}
                helperText={errors.usefulLifeYears}
                disabled={!formData.isDepreciable}
                inputProps={{ min: 0 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Current Value"
                name="currentValue"
                type="number"
                value={formData.currentValue}
                onChange={handleChange}
                error={!!errors.currentValue}
                helperText={errors.currentValue}
                inputProps={{ min: 0 }}
              />
            </Grid>
            <Grid item xs={12}>
              <div
                style={{ display: "flex", justifyContent: "flex-end", gap: 16 }}
              >
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  style={{   width: "80px",
              height: "50px",
              padding: "10px 0",
              fontWeight:'bold'
 }}
                >
                  UPDATE
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  onClick={()=>navigate(-1)}
                  style={{   width: "80px",
              height: "50px",
              padding: "10px 0",
              fontWeight:'bold'
 }}
                >
                 CANCEL
                </Button>
              </div>
            </Grid>
          </Grid>
        </form>
      </Paper>
     

      <Modal
  open={showSuccessModal}
  onClose={() => setShowSuccessModal(false)}
  aria-labelledby="success-modal-title"
  aria-describedby="success-modal-description"
>
  <Box
    sx={{
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: 400,
      bgcolor: 'background.paper',
      borderRadius: 2,
      boxShadow: 24,
      overflow: 'hidden',
    }}
  >
    {/* Header */}
    <Box sx={{ backgroundColor: '#0B1F40', padding: '12px 16px' }}>
      <Typography id="success-modal-title" variant="h6" sx={{ color: '#fff' }}>
       Item Updated ✅
      </Typography>
    </Box>

    {/* Content */}
    <Box sx={{ padding: '20px' }}>
      <Typography id="success-modal-description" sx={{ mb: 2 }}>
        The Item has been updated successfully.<br />
            <strong>Item Code:</strong> {responseData.itemCode}
      </Typography>

      {/* Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
        <Button
          onClick={() => {
            setShowSuccessModal(false);
            navigate(-1); // go back after closing modal
          }}
          variant="contained"
          color="primary"
        >
          OK
        </Button>
      </Box>
    </Box>
  </Box>
</Modal>

    </div>
  );
};

export default EditInventoryItem;
